import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const API_BASE = 'http://174.138.79.56:3001';

export function ProfileModal({ isOpen, onClose, profile, onSaved, filterOptions }) {
  const { token } = useAuth();

  const [income,        setIncome]        = useState('');
  const [householdSize, setHouseholdSize] = useState('');
  const [bedrooms,      setBedrooms]      = useState('');
  const [maxRent,       setMaxRent]       = useState('');
  const [borough,       setBorough]       = useState('');
  const [neighborhood,  setNeighborhood]  = useState('');
  const [listingType,   setListingType]   = useState('');
  const [saving,        setSaving]        = useState(false);
  const [error,         setError]         = useState('');

  // Pre-fill from existing profile when modal opens
  useEffect(() => {
    if (isOpen && profile) {
      setIncome(profile.annual_income        ? String(profile.annual_income)                       : '');
      setHouseholdSize(profile.household_size ? String(profile.household_size)                     : '');
      setBedrooms(profile.bedroom_types?.length ? String(Math.min(...profile.bedroom_types))       : '');
      setMaxRent('');
      setBorough(profile.boroughs?.[0]          || '');
      setNeighborhood(profile.neighborhoods?.[0] || '');
      setListingType(profile.listing_types?.[0]  || '');
    } else if (isOpen && !profile) {
      // Reset for new profile
      setIncome(''); setHouseholdSize(''); setBedrooms('');
      setMaxRent(''); setBorough(''); setNeighborhood(''); setListingType('');
    }
    setError('');
  }, [isOpen, profile]);

  if (!isOpen) return null;

  const handleSave = async () => {
    setError('');
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          annual_income:  income         ? parseFloat(income)         : null,
          household_size: householdSize  ? parseInt(householdSize)    : null,
          bedroom_types:  bedrooms !== '' ? [parseInt(bedrooms)]      : null,
          boroughs:       borough        ? [borough]                  : null,
          neighborhoods:  neighborhood   ? [neighborhood]             : null,
          listing_types:  listingType    ? [listingType]              : null,
        })
      });
      if (!res.ok) throw new Error('Failed to save profile');
      const saved = await res.json();
      onSaved(saved);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={s.header}>
          <div style={s.title}>Household Profile</div>
          <button style={s.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div style={s.subtitle}>
          Save your household details to quickly filter lotteries you're eligible for.
        </div>

        {/* Form */}
        <div style={s.body}>

          <div style={s.section}>HOUSEHOLD</div>
          <div style={s.row}>
            <div style={s.field}>
              <label style={s.label}>Annual Income</label>
              <input
                style={s.input}
                type="number"
                placeholder="e.g. 75000"
                value={income}
                onChange={e => setIncome(e.target.value)}
              />
            </div>
            <div style={s.field}>
              <label style={s.label}>Household Size</label>
              <select style={s.input} value={householdSize} onChange={e => setHouseholdSize(e.target.value)}>
                <option value="">Select</option>
                {[1,2,3,4,5,6,7,8].map(n =>
                  <option key={n} value={n}>{n} {n === 1 ? 'person' : 'people'}</option>
                )}
              </select>
            </div>
          </div>

          <div style={s.section}>UNIT PREFERENCES</div>
          <div style={s.row}>
            <div style={s.field}>
              <label style={s.label}>Min Bedrooms</label>
              <select style={s.input} value={bedrooms} onChange={e => setBedrooms(e.target.value)}>
                <option value="">Any</option>
                <option value="0">Studio</option>
                <option value="1">1 BR+</option>
                <option value="2">2 BR+</option>
                <option value="3">3 BR+</option>
              </select>
            </div>
            <div style={s.field}>
              <label style={s.label}>Max Rent / mo</label>
              <input
                style={s.input}
                type="number"
                placeholder="e.g. 2000"
                value={maxRent}
                onChange={e => setMaxRent(e.target.value)}
              />
            </div>
          </div>

          <div style={s.row}>
            <div style={s.field}>
              <label style={s.label}>Listing Type</label>
              <select style={s.input} value={listingType} onChange={e => setListingType(e.target.value)}>
                <option value="">Both</option>
                <option value="rental">Rental</option>
                <option value="sale">Sale</option>
              </select>
            </div>
          </div>

          <div style={s.section}>LOCATION</div>
          <div style={s.row}>
            <div style={s.field}>
              <label style={s.label}>Borough</label>
              <select style={s.input} value={borough} onChange={e => setBorough(e.target.value)}>
                <option value="">Any</option>
                {(filterOptions?.boroughs || []).map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div style={s.field}>
              <label style={s.label}>Neighborhood</label>
              <select style={s.input} value={neighborhood} onChange={e => setNeighborhood(e.target.value)}>
                <option value="">Any</option>
                {(filterOptions?.neighborhoods || []).map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          </div>

          {error && <div style={s.error}>{error}</div>}

          <div style={s.footer}>
            <button style={s.cancelBtn} onClick={onClose}>Cancel</button>
            <button
              style={{ ...s.saveBtn, opacity: saving ? 0.7 : 1 }}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : profile ? 'Update Profile' : 'Create Profile'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────
const s = {
  overlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(26,26,46,0.6)',
    zIndex: 2000,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  modal: {
    background: '#faf7f2',
    borderRadius: 8,
    width: 420,
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 8px 40px rgba(26,26,46,0.2)',
    fontFamily: "'DM Mono', monospace",
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '18px 20px 14px',
    background: '#1a1a2e',
    borderBottom: '2px solid #c84b31',
  },
  title: {
    fontFamily: "'Fraunces', serif",
    fontSize: 17, fontWeight: 600,
    color: '#f5f0e8',
  },
  closeBtn: {
    background: 'none', border: 'none',
    color: '#8a8070', fontSize: 14,
    cursor: 'pointer', padding: 4,
  },
  subtitle: {
    padding: '12px 20px',
    fontSize: 12, color: '#8a8070',
    borderBottom: '1px solid #d0c8b8',
    lineHeight: 1.5,
  },
  body: { padding: '16px 20px 20px' },
  section: {
    fontSize: 9, fontWeight: 500,
    letterSpacing: '0.1em',
    color: '#8a8070',
    marginBottom: 10, marginTop: 16,
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 10, marginBottom: 4,
  },
  field: {},
  label: {
    display: 'block',
    fontSize: 10, fontWeight: 500,
    letterSpacing: '0.06em', textTransform: 'uppercase',
    color: '#8a8070', marginBottom: 5,
  },
  input: {
    width: '100%', padding: '8px 10px',
    background: 'white', border: '1px solid #d0c8b8',
    borderRadius: 5,
    fontFamily: "'DM Mono', monospace",
    fontSize: 12, color: '#1a1a2e',
    outline: 'none', boxSizing: 'border-box',
  },
  error: {
    background: '#fde8e4', border: '1px solid #c84b31',
    borderRadius: 5, padding: '8px 12px',
    fontSize: 12, color: '#c84b31',
    margin: '12px 0',
  },
  footer: {
    display: 'flex', gap: 10,
    marginTop: 20,
  },
  cancelBtn: {
    flex: 1, padding: '10px',
    background: 'transparent',
    border: '1px solid #d0c8b8',
    borderRadius: 5,
    fontFamily: "'DM Mono', monospace",
    fontSize: 12, color: '#8a8070',
    cursor: 'pointer',
  },
  saveBtn: {
    flex: 2, padding: '10px',
    background: '#1a1a2e', color: '#f5f0e8',
    border: 'none', borderRadius: 5,
    fontFamily: "'DM Mono', monospace",
    fontSize: 12, fontWeight: 500,
    cursor: 'pointer',
  },
};
